import { meetsThreshold } from './logging.maps';
import type {
    LogEntry,
    LogLevel,
    MetaPredicates,
    OverlayStorage,
    PersistedOverlayRule,
    PersistedOverlayState,
} from './logging.types';

type MetaPredicateOperator = (actual: unknown, expected: unknown) => boolean;

/** Supported predicate operators, keyed by their name in rule objects. */
const META_PREDICATE_OPERATORS: Readonly<Record<string, MetaPredicateOperator>> = {
    eq: (actual, expected) => Object.is(actual, expected),
};

/**
 * Runtime console visibility overlay: a minimum-level override plus ordered
 * white/black rules (message substring or meta predicates). The last matching
 * rule wins, so sets can be carved down, and a whitelist match beats the
 * level. State persists via {@link OverlayStorage} with expiry, so stale
 * overrides self-heal.
 */
export class LogFilterOverlay {
    private minLevel?: LogLevel;
    private rules: PersistedOverlayRule[] = [];
    private storage?: OverlayStorage;

    /** Whether any override (level or rules) is currently set. */
    get isActive(): boolean {
        return this.minLevel !== undefined || this.rules.length > 0;
    }

    /** Attach a persistence backend and restore any state it holds. */
    attachStorage(storage: OverlayStorage): void {
        this.storage = storage;
        const persisted = storage.load();

        if (persisted) {
            this.minLevel = persisted.minLevel;
            this.rules = [...persisted.rules];
        }
    }

    /** Decide visibility for one entry on an overlay-respecting destination. */
    evaluate(entry: LogEntry, threshold: LogLevel): boolean {
        const defaultVisible = meetsThreshold(entry.level, this.minLevel ?? threshold);

        return this.rules.reduce(
            (visible, rule) => (ruleMatches(rule, entry) ? rule.list === 'white' : visible),
            defaultVisible
        );
    }

    /** Cheap pre-check: whether an entry at `level` could possibly be shown. */
    mightShow(level: LogEntry['level'], threshold: LogLevel): boolean {
        const hasWhitelist = this.rules.some((rule) => rule.list === 'white');

        return hasWhitelist || meetsThreshold(level, this.minLevel ?? threshold);
    }

    addMessageRule(list: 'black' | 'white', message: string): void {
        this.rules.push({ list, message });
        this.persist();
    }

    addMetaRule(list: 'black' | 'white', predicates: MetaPredicates): void {
        this.rules.push({ list, predicates });
        this.persist();
    }

    setMinimumLevel(level: LogLevel): void {
        this.minLevel = level;
        this.persist();
    }

    /** Remove all overlay settings and any persisted state. */
    reset(): void {
        this.minLevel = undefined;
        this.rules = [];
        this.storage?.clear();
    }

    private persist(): void {
        const state: PersistedOverlayState = { minLevel: this.minLevel, rules: this.rules };
        this.storage?.save(state);
    }
}

function ruleMatches(rule: PersistedOverlayRule, entry: LogEntry): boolean {
    if (rule.message !== undefined) {
        return entry.message.toLowerCase().includes(rule.message.toLowerCase());
    }

    if (rule.predicates !== undefined) {
        return metaMatches(rule.predicates, { ...entry.context, ...entry.meta });
    }

    return false;
}

/** Whether every predicate matches the merged context + meta of the entry. */
function metaMatches(predicates: MetaPredicates, values: Record<string, unknown>): boolean {
    return Object.entries(predicates).every(([property, operators]) =>
        Object.entries(operators).every(([operatorName, expected]) => {
            const operator = META_PREDICATE_OPERATORS[operatorName];

            return operator !== undefined && operator(values[property], expected);
        })
    );
}
